import * as Prismic from '@prismicio/client';
import { tagContextMatches } from '../../utils/tagContextMatches';
import {
  AnyRegularField,
  BuildQueryURLArgs,
  ClientConfig,
  GroupField,
  PrismicDocument,
  SliceZone,
} from '@prismicio/client';
import { linkResolver } from '../../utils/prismic';
import { IPrismicService, PrismicLinkContext } from '@monofront/types';

export type PreviewData = string | false | object | undefined;

export type QueryOptions = Partial<BuildQueryURLArgs> & {
  fetchOptions?: Prismic.RequestInitLike;
  signal?: Prismic.AbortSignalLike;
};

export interface IPrismicConfig {
  apiUrl?: string;
  apiKey: string;
  repository: string;
}

type PrismicDocumentProps = Record<
  string,
  AnyRegularField | GroupField | SliceZone
>;

export class PrismicService implements IPrismicService {
  private prismicConfig: IPrismicConfig;
  private GRAPHQL_API_URL: string;
  private logger: any;

  constructor(prismicConfig: IPrismicConfig, logger: any) {
    this.prismicConfig = prismicConfig;
    this.GRAPHQL_API_URL = prismicConfig.apiUrl
      ? `${prismicConfig.apiUrl}/graphql`
      : `https://${prismicConfig.repository}.prismic.io/graphql`;
    this.logger = logger;
  }

  private createClientOptions = (
    endpoint: string,
    prismicAccessToken: string,
    previewData?: PreviewData,
    defaultLang?: string,
  ): Prismic.ClientConfig => {
    const previewDataOption =
      typeof previewData === 'object' ? { ...previewData } : {};
    const accessTokenOption = prismicAccessToken
      ? { accessToken: prismicAccessToken }
      : {};

    return {
      documentAPIEndpoint: endpoint,
      ...previewDataOption,
      ...accessTokenOption,
      defaultParams: { lang: defaultLang },
    };
  };

  public getClient = (
    previewData?: PreviewData,
    repository?: string,
    defaultLang?: string,
  ): Prismic.Client => {
    const endpoint = this.prismicConfig.apiUrl
      ? `${this.prismicConfig.apiUrl}/api/v2`
      : Prismic.getRepositoryEndpoint(
          repository ?? this.prismicConfig.repository,
        );

    const clientOptions: ClientConfig = this.createClientOptions(
      endpoint,
      this.prismicConfig.apiKey,
      previewData,
      defaultLang,
    );

    return Prismic.createClient(this.prismicConfig.repository, clientOptions);
  };

  public getRedirectUrl = (token: string, documentId: string) => {
    return this.getClient().resolvePreviewURL({
      previewToken: token,
      documentID: documentId,
      defaultURL: '/',
      linkResolver: (doc) => linkResolver(doc, PrismicLinkContext.SHOP),
    });
  };

  public async getDocumentsByCustomTypeName<
    T extends Record<string, AnyRegularField | GroupField | SliceZone>,
    Document extends PrismicDocument = PrismicDocument<T>,
  >(
    customTypeName: string,
    previewData?: PreviewData,
    options: QueryOptions = {},
  ) {
    let page = 1;
    let hasPageToFetch = true;
    // TODO: use the `Document` generic once we are ready to migrate types
    const prismicDocuments: PrismicDocument[] = [];

    while (hasPageToFetch) {
      const { results, total_pages }: Prismic.Query<Document> =
        await this.getClient(previewData).get<Document>({
          page,
          filters: [Prismic.filter.at('document.type', customTypeName)],
          ...options,
        });

      prismicDocuments.push(...results);
      hasPageToFetch = page < total_pages;
      page++;
    }

    return { results: prismicDocuments };
  }

  public async getDocumentById<
    T extends Record<string, AnyRegularField | GroupField | SliceZone>,
    Document extends PrismicDocument = PrismicDocument<T>,
  >(
    id: string,
    previewData?: PreviewData,
    options: QueryOptions = {},
  ): Promise<Document> {
    return this.getClient(previewData).getByID<Document>(id, options);
  }

  public async getSingleDocumentByCustomTypeName<
    T extends Record<string, AnyRegularField | GroupField | SliceZone>,
    Document extends PrismicDocument = PrismicDocument<T>,
  >(
    customTypeName: string,
    previewData?: PreviewData,
    options: QueryOptions = {},
  ) {
    return this.getClient(previewData).getSingle<Document>(
      customTypeName,
      options,
    );
  }

  public async getDocumentByUid<
    T extends PrismicDocumentProps | Prismic.PrismicDocumentWithUID,
  >(
    customTypeName: string,
    uid: string,
    previewData?: PreviewData,
    options: QueryOptions = {},
  ): Promise<T extends PrismicDocumentProps ? PrismicDocument<T> : T> {
    return this.getClient(previewData).getByUID<
      T extends PrismicDocumentProps ? PrismicDocument<T> : T
    >(customTypeName, uid, options);
  }

  public async getDocumentsByUids<
    TDocument extends Prismic.PrismicDocument<
      Record<string, any>,
      string,
      string
    >,
  >(
    customTypeName: string,
    uids: string[],
    previewData?: PreviewData,
    options: QueryOptions = {},
  ) {
    let page = 1;
    let hasPageToFetch = true;
    const prismicDocuments: TDocument[] = [];

    while (hasPageToFetch) {
      const { results, total_pages } = await this.getClient(
        previewData,
      ).getByUIDs(customTypeName, uids, {
        ...options,
        page,
      });

      prismicDocuments.push(...(results as TDocument[]));
      hasPageToFetch = page < total_pages;
      page++;
    }

    return { results: prismicDocuments };
  }

  public getCollectionProductBasket = ({
    tag,
    previewData,
    options = {},
  }: {
    tag: string;
    previewData?: PreviewData;
    options?: QueryOptions;
  }) => {
    return this.getClient(previewData).get({
      filters: [
        Prismic.filter.at('document.type', 'productbasket'),
        Prismic.filter.at('document.tags', [tag]),
      ],
      ...options,
    });
  };

  public fetchAPIGraphql = async (
    query: any,
    { previewData, variables }: { previewData?: any; variables?: any } = {},
  ) => {
    const prismicAPI = await this.getClient(); //TODO maybe this should not change
    const masterRef = await prismicAPI.getMasterRef();

    const res = await fetch(
      `${this.GRAPHQL_API_URL}?query=${encodeURI(query)}&variables=${encodeURI(
        JSON.stringify(variables),
      )}`,

      {
        headers: {
          'Prismic-Ref': previewData?.ref || masterRef,
          'Content-Type': 'application/json',
          Authorization: `Token ${this.prismicConfig.apiKey}`,
        },
      },
    );

    if (res.status !== 200) {
      this.logger.info(await res.text());
      throw new Error('Failed to fetch API');
    }

    const json = await res.json();

    if (json.errors) {
      this.logger.error(json.errors);
      throw new Error('Failed to fetch API');
    }

    return json.data;
  };

  public tagContextMatches = tagContextMatches;

  private listeners = [] as Array<() => void>;

  public addConfigListener = (listener: () => void) => {
    this.listeners.push(listener);
  };

  public removeConfigListener = (listener: () => void) => {
    const pos = this.listeners.findIndex(listener);

    if (pos >= 0) {
      this.listeners.splice(pos, 1);
    }
  };

  public notifyConfigChanged = () => {
    setImmediate(() =>
      this.listeners.forEach(() => {
        this.listeners.forEach((listener) => listener());
      }),
    );
  };
}
